import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { PremiumHeader } from '@/components/premium/PremiumHeader';
import { SEO } from '@/components/SEO';
import { MapPin, Calendar, Users, ArrowLeft, ChevronLeft, ChevronRight, Building2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PROJECTS_DATA } from './Projects';

export default function ProjectDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  const project = PROJECTS_DATA.find(p => p.slug === slug);

  useEffect(() => {
    if (!project && PROJECTS_DATA.length > 0) {
      navigate('/projects');
    }
  }, [project, navigate]);

  if (!project) {
    return (
      <div className="min-h-screen bg-white">
        <PremiumHeader />
        <div className="pt-32 pb-16 text-center">
          <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-[#1a1a1a] mb-4">Project Not Found</h1>
          <p className="text-gray-600 mb-8">This project doesn't exist or has been removed.</p>
          <Link to="/projects">
            <Button className="bg-[#c5a059] hover:bg-[#b8934d] text-[#1a1a1a]">
              View All Projects
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleSliderMove = (clientX, containerRect) => {
    if (!isDragging) return;
    const position = ((clientX - containerRect.left) / containerRect.width) * 100;
    setSliderPosition(Math.min(Math.max(position, 5), 95));
  };

  // Find adjacent projects for navigation
  const currentIndex = PROJECTS_DATA.findIndex(p => p.slug === slug);
  const prevProject = currentIndex > 0 ? PROJECTS_DATA[currentIndex - 1] : null;
  const nextProject = currentIndex < PROJECTS_DATA.length - 1 ? PROJECTS_DATA[currentIndex + 1] : null;

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={`${project.name} | Workspace Transformation | Modular Furniture Store`}
        description={project.description}
      />
      <PremiumHeader />
      
      {/* Breadcrumb */}
      <div className="pt-24 pb-4 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-8">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-gray-500 hover:text-[#c5a059]">Home</Link>
            <span className="text-gray-400">/</span>
            <Link to="/projects" className="text-gray-500 hover:text-[#c5a059]">Projects</Link>
            <span className="text-gray-400">/</span>
            <span className="text-[#1a1a1a] font-medium">{project.name}</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-8">
          <Link 
            to="/projects" 
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#c5a059] mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Projects
          </Link>
          
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Project Info */}
            <div>
              <h1 
                className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1a1a1a] mb-4"
                
              >
                {project.name}
              </h1>
              
              {project.client && (
                <p className="text-xl text-[#c5a059] font-medium mb-4">{project.client}</p>
              )}
              
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                {project.description}
              </p>

              {/* Project Meta */}
              <div className="flex flex-wrap gap-6 mb-8">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#c5a059]" />
                  <span className="text-gray-700">{project.location}</span>
                </div>
                {project.completionDate && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-[#c5a059]" />
                    <span className="text-gray-700">{project.completionDate}</span>
                  </div>
                )}
                {project.teamSize && (
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#c5a059]" />
                    <span className="text-gray-700">{project.teamSize}</span>
                  </div>
                )}
              </div>

              {/* Challenge & Solution */}
              {(project.challenge || project.solution) && (
                <div className="space-y-6">
                  {project.challenge && (
                    <div className="bg-white p-6 rounded-lg border border-gray-200">
                      <h3 className="text-lg font-bold text-[#1a1a1a] mb-2">The Challenge</h3>
                      <p className="text-gray-600">{project.challenge}</p>
                    </div>
                  )}
                  {project.solution && (
                    <div className="bg-[#c5a059]/10 p-6 rounded-lg border border-[#c5a059]/20">
                      <h3 className="text-lg font-bold text-[#1a1a1a] mb-2">Our Solution</h3>
                      <p className="text-gray-600">{project.solution}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Before/After Slider */}
            <div className="lg:sticky lg:top-24">
              <div 
                className="relative aspect-[4/3] rounded-xl overflow-hidden shadow-2xl cursor-ew-resize select-none bg-gray-200"
                onMouseDown={() => setIsDragging(true)}
                onMouseUp={() => setIsDragging(false)}
                onMouseLeave={() => setIsDragging(false)}
                onMouseMove={(e) => handleSliderMove(e.clientX, e.currentTarget.getBoundingClientRect())}
                onTouchStart={() => setIsDragging(true)}
                onTouchEnd={() => setIsDragging(false)}
                onTouchMove={(e) => handleSliderMove(e.touches[0].clientX, e.currentTarget.getBoundingClientRect())}
              >
                {/* After Image (Background) */}
                <img 
                  src={project.afterImage}
                  alt={`${project.name} - After`}
                  className="absolute inset-0 w-full h-full object-cover"
                  draggable={false}
                />
                
                {/* Before Image (Clipped) */}
                <div 
                  className="absolute inset-0 overflow-hidden"
                  style={{ width: `${sliderPosition}%` }}
                >
                  <img 
                    src={project.beforeImage}
                    alt={`${project.name} - Before`}
                    className="absolute inset-0 h-full object-cover"
                    style={{ 
                      width: `${100 / (sliderPosition / 100)}%`,
                      maxWidth: 'none'
                    }}
                    draggable={false}
                  />
                </div>

                {/* Slider Handle */}
                <div 
                  className="absolute top-0 bottom-0 w-1 bg-white shadow-lg"
                  style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
                >
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center border-2 border-[#c5a059]">
                    <ChevronLeft className="w-4 h-4 text-[#c5a059] -mr-1" />
                    <ChevronRight className="w-4 h-4 text-[#c5a059] -ml-1" />
                  </div>
                </div>

                {/* Labels */}
                <div className="absolute top-4 left-4 bg-black/80 text-white text-sm px-4 py-2 rounded-full font-medium">
                  BEFORE
                </div>
                <div className="absolute top-4 right-4 bg-[#c5a059] text-[#1a1a1a] text-sm px-4 py-2 rounded-full font-medium">
                  AFTER
                </div>

                {/* Instruction */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-4 py-2 rounded-full">
                  Drag to compare
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Highlights */}
      {project.highlights && project.highlights.length > 0 && (
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4 sm:px-8">
            <h2 className="text-2xl font-bold text-[#1a1a1a] mb-8">Key Highlights</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {project.highlights.map((highlight, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <Check className="w-5 h-5 text-[#c5a059] mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{highlight}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Project Navigation */}
      {(prevProject || nextProject) && (
        <section className="py-12 bg-gray-50 border-t">
          <div className="container mx-auto px-4 sm:px-8">
            <div className="flex justify-between items-center">
              {prevProject ? (
                <Link 
                  to={`/projects/${prevProject.slug}`}
                  className="flex items-center gap-3 text-gray-600 hover:text-[#c5a059] transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <div>
                    <p className="text-xs text-gray-400 uppercase">Previous Project</p>
                    <p className="font-medium">{prevProject.name}</p>
                  </div>
                </Link>
              ) : <div />}
              
              {nextProject ? (
                <Link 
                  to={`/projects/${nextProject.slug}`}
                  className="flex items-center gap-3 text-gray-600 hover:text-[#c5a059] transition-colors text-right"
                >
                  <div>
                    <p className="text-xs text-gray-400 uppercase">Next Project</p>
                    <p className="font-medium">{nextProject.name}</p>
                  </div>
                  <ArrowLeft className="w-5 h-5 rotate-180" />
                </Link>
              ) : <div />}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-[#f8f7f4]">
        <div className="container mx-auto px-4 sm:px-8 text-center">
          <h2 
            className="text-3xl sm:text-4xl font-bold text-[#1a1a1a] mb-4"
            
          >
            Want a Similar Transformation?
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto mb-8">
            Let our experts help you create the perfect workspace for your team.
            Get a free consultation and personalized quote.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/bulk-inquiry">
              <Button className="bg-[#c5a059] hover:bg-[#b8934d] text-[#1a1a1a] px-8">
                Get Free Consultation
              </Button>
            </Link>
            <Link to="/products">
              <Button variant="outline" className="border-[#1a1a1a] text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white px-8">
                Browse Products
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
